import React, { useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { validateMarkdownFile } from '../../../utils/markdownValidation.ts';
import { useMarkdownFolderResolver } from '../../../hooks/useMarkdownFolderResolver.ts';
import type {
	ImportStrategyComponentPropsType,
	ImportStrategyComponentType,
	ImportStrategyType,
} from '../../types.ts';
import { ActiveImports, IMPORT_FOLDER_NAMES } from '../importStrategyDefinitions.ts';

const i18n = defineMessages({
	fileInputLabel: {
		id: 'import-from-sources.markdown.file-input.label',
		defaultMessage: 'Choose Markdown files',
		description: 'Accessible label for the hidden Markdown file picker',
	},
	fallbackTitle: {
		id: 'import-from-sources.markdown.fallback-title',
		defaultMessage: 'Imported Markdown',
		description: 'Fallback page title when a Markdown file has no heading or usable filename',
	},
});

const createAttemptId = (): string => {
	if (typeof globalThis.crypto?.randomUUID === 'function') {
		return globalThis.crypto.randomUUID();
	}

	if (typeof globalThis.crypto?.getRandomValues !== 'function') {
		return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
	}

	const values = new Uint32Array(4);
	globalThis.crypto.getRandomValues(values);
	return Array.from(values, (value) => value.toString(16).padStart(8, '0')).join('-');
};

export const ImportFromMarkdown: ImportStrategyComponentType = ({
	onStartStrategy,
	onDocumentSelect,
	onDocumentSelectError,
	registerTrigger,
	children,
}: ImportStrategyComponentPropsType) => {
	const { formatMessage } = useIntl();
	const resolveMarkdownFolder = useMarkdownFolderResolver();
	const inputRef = useRef<HTMLInputElement>(null);
	const attemptIdByFileRef = useRef(new WeakMap<File, string>());
	const validatedTextByFileRef = useRef(new WeakMap<File, string>());

	const pickDocument = () => {
		inputRef.current?.dispatchEvent(
			new MouseEvent('click', {
				bubbles: false,
				cancelable: true,
				view: window,
			}),
		);
	};

	const validateMarkdownDocuments: NonNullable<
		ImportStrategyType['validateSingleUploadBeforeProgress']
	> = async (documents) => {
		const files = documents as File[];
		const results = await Promise.all(files.map((file) => validateMarkdownFile(file)));

		if (results.some((result) => !result.ok)) {
			onDocumentSelectError();
			return false;
		}

		results.forEach((result) => {
			if (result.ok) {
				validatedTextByFileRef.current.set(result.file, result.text);
			}
		});

		return files;
	};

	const folderName = formatMessage(IMPORT_FOLDER_NAMES[ActiveImports.IMPORT_FROM_MARKDOWN]);
	const strategy: ImportStrategyType = {
		key: ActiveImports.IMPORT_FROM_MARKDOWN,
		folderName,
		pickDocument,
		createsSeparatePage: true,
		bulkImportMode: 'independent-pages',
		validateSingleUploadBeforeProgress: validateMarkdownDocuments,
		validateBulkUploadBeforeProgress: validateMarkdownDocuments,
		validateBulkBeforeUpload: async (documents) => {
			const files = documents.filter((document): document is File => document !== null);
			return files.length ? files : false;
		},
		resolveParentPage: ({ spaceId, spaceKey, signal }) =>
			resolveMarkdownFolder({ spaceId, spaceKey, folderName, signal }),
		uploadSingle: async (document, options) => {
			let markdown = validatedTextByFileRef.current.get(document);
			validatedTextByFileRef.current.delete(document);

			if (markdown === undefined) {
				const validation = await validateMarkdownFile(document);
				if (!validation.ok) {
					throw new Error(`Markdown validation failed: ${validation.errorCode}`);
				}
				markdown = validation.text;
			}

			const { convertMarkdownToAdf } = await import(
				/* webpackChunkName: "loadable-markdown-import-conversion" */ '../../../services/MarkdownConversionAdapter.ts'
			);
			const conversion = await convertMarkdownToAdf({
				markdown,
				fileName: document.name,
				fallbackTitle: formatMessage(i18n.fallbackTitle),
				signal: options.signal,
			});
			const { createMarkdownDraft } = await import(
				/* webpackChunkName: "loadable-markdown-import-create" */ '../../../services/MarkdownImportService.ts'
			);
			const data = await createMarkdownDraft({
				spaceId: options.spaceId,
				spaceKey: options.spaceKey,
				parentPageId: options.parentPageId,
				title: conversion.title,
				adf: conversion.adf,
				subtype: 'live',
				attemptId: attemptIdByFileRef.current.get(document) ?? createAttemptId(),
				signal: options.signal,
				onCreateDispatch: options.onCreateDispatch,
			});

			return { data, warnings: conversion.warnings };
		},
	};
	const strategyRef = useRef(strategy);
	strategyRef.current = strategy;

	const start = useCallback(() => onStartStrategy(strategyRef), [onStartStrategy]);

	useEffect(() => {
		registerTrigger?.({ start });
	}, [registerTrigger, start]);

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.stopPropagation();
		event.preventDefault();

		const files = Array.from(event.target.files ?? []);
		event.target.value = '';
		if (files.length > 0) {
			files.forEach((file) => attemptIdByFileRef.current.set(file, createAttemptId()));
			onDocumentSelect(files);
		}
	};

	return (
		<>
			<VisuallyHidden>
				<input
					ref={inputRef}
					type="file"
					accept=".md,.markdown,.mdown"
					aria-label={formatMessage(i18n.fileInputLabel)}
					multiple
					onChange={handleFileChange}
					data-testid="import-from-markdown-frontend-file-input"
				/>
			</VisuallyHidden>
			{React.cloneElement(children as React.ReactElement, {
				...(children as React.ReactElement).props,
				onItemClick: (data: unknown) => {
					start();
					(children as React.ReactElement).props.onItemClick?.(data);
				},
				onClick: (event: React.MouseEvent) => {
					start();
					(children as React.ReactElement).props.onClick?.(event);
				},
			})}
		</>
	);
};
